"use client"

import { useState } from "react"
import { DashboardLayout } from "../../../components/dashboard-layout"
import { Card, CardContent } from "../../../components/ui/card"
import { Button } from "../../../components/ui/button"
import { useTheme } from "../../../contexts/theme-provider"
import { motion } from "framer-motion"
import { Mail, Copy, Check, Clock, Shield, Headset } from "lucide-react"

const SUPPORT_EMAIL = "support@exactomarkglobal.com"

export default function SupportPage() {
  const { theme } = useTheme()
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(SUPPORT_EMAIL)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <DashboardLayout>
      <div
        className={`min-h-screen p-4 md:p-8 ${
          theme === "dark"
            ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
            : "bg-gradient-to-br from-slate-50 via-white to-blue-50"
        }`}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full space-y-6 md:space-y-8"
        >
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className={`text-3xl md:text-4xl font-black bg-clip-text text-transparent mb-2 ${
                theme === "dark"
                  ? "bg-gradient-to-r from-white via-blue-100 to-purple-100"
                  : "bg-gradient-to-r from-slate-900 via-blue-900 to-purple-900"
              }`}
            >
              Support
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className={`font-medium text-sm md:text-base ${
                theme === "dark" ? "text-slate-300" : "text-slate-600 dark:text-slate-400"
              }`}
            >
              Need help? Reach out to us at the email below.
            </motion.p>
          </motion.div>

          {/* Contact info cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8"
          >
            {[
              {
                icon: Clock,
                title: "Response Time",
                value: "Within 24 hours",
                color: "from-green-500 to-emerald-600",
                bgColor: theme === "dark" ? "from-green-950/30 to-emerald-950/30" : "from-green-50 dark:from-green-900/30 to-emerald-50 dark:to-emerald-900/30",
              },
              {
                icon: Shield,
                title: "Secure & Private",
                value: "Your data is safe",
                color: "from-purple-500 to-pink-600",
                bgColor: theme === "dark" ? "from-purple-950/30 to-pink-950/30" : "from-purple-50 dark:from-purple-900/30 to-pink-50 dark:to-pink-900/30",
              },
              {
                icon: Headset,
                title: "Dedicated Team",
                value: "Here to help you",
                color: "from-blue-500 to-cyan-600",
                bgColor: theme === "dark" ? "from-blue-950/30 to-cyan-950/30" : "from-blue-50 dark:from-blue-900/30 to-cyan-50 dark:to-cyan-900/30",
              },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + index * 0.1, duration: 0.4 }}
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <Card className={`bg-gradient-to-br ${stat.bgColor} border-0 shadow-lg md:shadow-xl ${
                  theme === "dark" ? "shadow-black/20" : "shadow-black/5"
                }`}>
                  <CardContent className="p-6 md:p-8">
                    <div className="flex items-center gap-4 md:gap-6">
                      <div
                        className={`w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center shadow-lg md:shadow-xl`}
                      >
                        <stat.icon className="h-7 w-7 md:h-9 md:w-9 text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className={`text-sm md:text-base font-bold mb-1.5 ${
                          theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"
                        }`}>
                          {stat.title}
                        </p>
                        <p className={`text-lg md:text-2xl font-black bg-gradient-to-r ${stat.color} bg-clip-text text-transparent truncate`}>
                          {stat.value}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Email card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <Card className={`backdrop-blur-xl shadow-lg md:shadow-xl border ${
              theme === "dark"
                ? "bg-slate-800/70 border-slate-700/50 shadow-black/20"
                : "bg-white/70 border-slate-200/50 shadow-black/5"
            }`}>
              <CardContent className="p-6 md:p-10">
                <div className="flex flex-col items-center text-center space-y-6">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                    <Mail className="h-7 w-7 md:h-9 md:w-9 text-white" />
                  </div>

                  <div className="space-y-2">
                    <h2 className={`text-lg md:text-xl font-bold ${
                      theme === "dark" ? "text-white" : "text-slate-900 dark:text-white"
                    }`}>
                      Contact Email
                    </h2>
                    <p className={`text-sm md:text-base font-medium ${
                      theme === "dark" ? "text-slate-400" : "text-slate-500 dark:text-slate-400"
                    }`}>
                      Click to copy or email us directly
                    </p>
                  </div>

                  {/* Copyable email */}
                  <div className="w-full flex flex-col sm:flex-row items-stretch gap-3">
                    <div
                      onClick={handleCopy}
                      className={`flex-1 flex items-center justify-center gap-3 px-4 md:px-6 py-3 md:py-4 rounded-xl border-2 cursor-pointer transition-all duration-200 select-none ${
                        copied
                          ? "border-green-500/50 bg-green-500/10"
                          : theme === "dark"
                            ? "border-slate-700/50 bg-slate-900/50 hover:border-blue-500/50 hover:bg-blue-500/5"
                            : "border-slate-300/50 bg-white/50 hover:border-blue-500/50 hover:bg-blue-50/50"
                      }`}
                    >
                      <span className={`text-base md:text-xl font-bold break-all ${
                        theme === "dark" ? "text-white" : "text-slate-900 dark:text-white"
                      }`}>
                        {SUPPORT_EMAIL}
                      </span>
                      <div className="flex-shrink-0">
                        {copied ? (
                          <Check className="h-5 w-5 text-green-500 dark:text-green-400" />
                        ) : (
                          <Copy className={`h-5 w-5 ${theme === "dark" ? "text-slate-400" : "text-slate-500 dark:text-slate-400"}`} />
                        )}
                      </div>
                    </div>
                  </div>

                  {copied && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-sm font-bold text-green-500 dark:text-green-400"
                    >
                      Copied to clipboard!
                    </motion.p>
                  )}

                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      asChild
                      className="!bg-gradient-to-r !from-blue-500 !to-purple-600 !text-white !font-bold !px-8 !py-3 !rounded-xl !shadow-lg !shadow-blue-500/25 hover:!shadow-blue-500/40 !transition-all !duration-200 !cursor-pointer"
                    >
                      <a href={`mailto:${SUPPORT_EMAIL}`}>
                        <Mail className="!h-4 !w-4 !mr-2" />
                        Send Email
                      </a>
                    </Button>
                  </motion.div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </DashboardLayout>
  )
}
